import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import EnhancedQuestionDisplay from './EnhancedQuestionDisplay';
import UniversalHeader from './UniversalHeader';

// Add consistent styling - MathCampus Teal Design System
const addConsistentStyles = () => {
  if (document.getElementById('practice-session-styles')) return;

  const style = document.createElement('style');
  style.id = 'practice-session-styles';
  style.textContent = `
    .font-poppins { font-family: 'Poppins', sans-serif; }
    .font-opensans { font-family: 'Open Sans', sans-serif; }
    .text-primary { color: var(--mc-primary, #1CB7C8); }
    .bg-primary { background-color: var(--mc-primary, #1CB7C8); }
    .text-accent { color: var(--mc-primary, #1CB7C8); }
    .bg-accent { background-color: var(--mc-primary, #1CB7C8); }
    .bg-bg-cream { background-color: var(--mc-gray-100, #F5F5F7); }
    .text-text-dark { color: var(--mc-charcoal, #333333); }
    .border-primary { border-color: var(--mc-primary, #1CB7C8); }
    .hover\\:bg-primary:hover { background-color: var(--mc-primary-dark, #159BA9); }
    .hover\\:text-primary:hover { color: var(--mc-primary, #1CB7C8); }
    .hover\\:bg-teal-600:hover { background-color: var(--mc-primary-dark, #159BA9); }
  `;
  document.head.appendChild(style);
};

const PracticeSession = ({ sessionData, onComplete, onBack }) => {
  const { user } = useAuth();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(null); // Start with null instead of 0
  const [isTimerActive, setIsTimerActive] = useState(false); // Start with false
  const [showResults, setShowResults] = useState(false);
  const [sessionScore, setSessionScore] = useState(0);
  const [startTime, setStartTime] = useState(new Date());
  const [isInitialized, setIsInitialized] = useState(false); // Add initialization flag
  const [isPaused, setIsPaused] = useState(false);
  const [totalTimeSpent, setTotalTimeSpent] = useState(0); // Track actual time spent
  const [debugLogged, setDebugLogged] = useState(false); // Prevent repeated debug logs

  const questions = sessionData?.questions || [];
  const sessionInfo = sessionData?.session || {};
  const totalDuration = (sessionInfo.duration_minutes || 15) * 60; // Convert to seconds, default to 15 minutes

  useEffect(() => {
    addConsistentStyles();
    
    // Add Google Fonts
    if (!document.getElementById('practice-session-fonts')) {
      const fontLink = document.createElement('link');
      fontLink.id = 'practice-session-fonts';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Open+Sans:wght@400;500;600&display=swap';
      fontLink.rel = 'stylesheet';
      document.head.appendChild(fontLink);
    }
    
    // Debug: Log session data structure to understand the issue (runs once)
    if (!debugLogged) {
      console.log('🔍 INIT: Full sessionData structure:', JSON.stringify(sessionData, null, 2));
      console.log('🔍 INIT: Extracted questions count:', questions.length);
      console.log('🔍 INIT: Expected questions from session info:', sessionInfo.questions_count);
      console.log('🔍 INIT: Sample question structure:', questions[0]);
      console.log('🔍 INIT: All questions:', questions);
      console.log('🔍 INIT: Initializing session:', sessionInfo);
      setDebugLogged(true);
    }
    
    // Check if this is a resumed session
    const isResumed = sessionInfo.is_paused && sessionInfo.current_question_index !== undefined;
    
    if (isResumed) {
      console.log('🔄 Resuming paused session');
      setCurrentQuestionIndex(sessionInfo.current_question_index || 0);
      setAnswers(sessionInfo.answers_so_far || {});
      setTotalTimeSpent(sessionInfo.time_spent_seconds || 0);
      // Adjust remaining time based on time already spent
      const adjustedDuration = totalDuration - (sessionInfo.time_spent_seconds || 0);
      setTimeRemaining(Math.max(adjustedDuration, 60)); // At least 1 minute remaining
    } else {
      setTimeRemaining(totalDuration);
      setTotalTimeSpent(0);
    }
    
    setStartTime(new Date());
    setIsInitialized(true);
    setIsTimerActive(true); // Activate timer after initialization
  }, [totalDuration, sessionInfo]);

  useEffect(() => {
    // Timer countdown - only run if initialized and timer is positive
    if (isInitialized && isTimerActive && timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (isInitialized && timeRemaining === 0 && isTimerActive) {
      // Time's up - auto finish session
      console.log('🔍 Timer reached 0, finishing session');
      finishSession();
    }
  }, [timeRemaining, isTimerActive, isInitialized]);

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '--:--';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleAnswerSelect = (questionIndex, answerIndex) => {
    console.log(`🔍 Answer selected: Q${questionIndex} = ${answerIndex}`);
    setAnswers(prev => {
      const newAnswers = {
        ...prev,
        [questionIndex]: answerIndex
      };
      console.log('🔍 Updated answers object:', newAnswers);
      return newAnswers;
    });
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // All questions answered
      finishSession();
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const finishSession = () => {
    console.log('🔍 finishSession called');
    console.log('🔍 Current answers when finishing:', answers);
    console.log('🔍 Current question index:', currentQuestionIndex);
    console.log('🔍 Total questions:', questions.length);
    setIsTimerActive(false);
    calculateAndSaveResults();
  };

  const calculateAndSaveResults = async () => {
    // Calculate score
    let correctAnswers = 0;
    const totalAnswered = Object.keys(answers).length;
    
    console.log('🔍 DEBUG: Starting score calculation...');
    console.log('🔍 DEBUG: answers object:', answers);
    console.log('🔍 DEBUG: questions array length:', questions.length);
    console.log('🔍 DEBUG: First question structure:', questions[0]);
    
    questions.forEach((question, index) => {
      const userAnswer = answers[index];
      console.log(`🔍 DEBUG Q${index}: userAnswer=${userAnswer}, userAnswer type=${typeof userAnswer}`);
      
      if (userAnswer !== undefined) {
        // Use correct_index if available, otherwise convert letter-based correct_answer to index
        let correctAnswerIndex;
        if (question.correct_index !== undefined) {
          correctAnswerIndex = question.correct_index;
        } else if (typeof question.correct_answer === 'string' && question.correct_answer.match(/^[A-D]$/)) {
          // Convert letter-based answers (A, B, C, D) to indices (0, 1, 2, 3)
          correctAnswerIndex = question.correct_answer.charCodeAt(0) - 'A'.charCodeAt(0);
        } else {
          // Try to find exact text match in options
          correctAnswerIndex = question.options.indexOf(question.correct_answer);
        }
          
        console.log(`🔍 DEBUG Q${index}: correct_index=${question.correct_index}, correct_answer="${question.correct_answer}"`);
        console.log(`🔍 DEBUG Q${index}: options=`, question.options);
        console.log(`🔍 DEBUG Q${index}: calculated correctAnswerIndex=${correctAnswerIndex}`);
        console.log(`🔍 DEBUG Q${index}: match check: ${userAnswer} === ${correctAnswerIndex} = ${userAnswer === correctAnswerIndex}`);
        
        if (userAnswer === correctAnswerIndex) {
          correctAnswers++;
          console.log(`🔍 DEBUG Q${index}: ✅ CORRECT! Total correct now: ${correctAnswers}`);
        } else {
          console.log(`🔍 DEBUG Q${index}: ❌ WRONG`);
        }
      } else {
        console.log(`🔍 DEBUG Q${index}: No answer provided`);
      }
    });
    
    console.log('🔍 DEBUG: Final correctAnswers:', correctAnswers, 'totalAnswered:', totalAnswered);

    const score = totalAnswered > 0 ? Math.round((correctAnswers / totalAnswered) * 100) : 0;
    setSessionScore(score);

    // Save session results to backend
    try {
      const endTime = new Date();
      const actualDuration = Math.round((endTime - startTime) / 1000 / 60); // in minutes

      const resultData = {
        session_id: sessionInfo.id,
        user_id: user?.id,
        questions_answered: totalAnswered,
        correct_answers: correctAnswers,
        score: score,
        actual_duration_minutes: actualDuration,
        answers: answers,
        completed_at: endTime.toISOString(),
        subtopic_id: sessionInfo.subtopic_id,
        difficulty_level: sessionInfo.difficulty_level,
        learning_path_id: sessionInfo.learning_path_id  // Add this for mock sessions
      };

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Check if this is a Learning Path practice session by looking at session data
      const isLearningPathSession = sessionInfo.subtopic_id || sessionInfo.learning_path_id;
      const completeEndpoint = isLearningPathSession 
        ? `${backendUrl}/api/learning-path/practice/complete-session`
        : `${backendUrl}/api/practice/complete-session`;
        
      console.log('🔍 Using complete endpoint:', completeEndpoint, 'for session:', sessionInfo);
      console.log('🔍 Sending completion data:', resultData);
      
      const response = await fetch(completeEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(resultData)
      });

      console.log('🔍 Completion response status:', response.status);
      console.log('🔍 Completion response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 Completion error response body:', errorText);
        
        // If learning path endpoint fails with 404, try regular practice endpoint as fallback
        if (response.status === 404 && isLearningPathSession) {
          console.log('🔄 Learning path endpoint failed, trying regular practice endpoint...');
          const fallbackEndpoint = `${backendUrl}/api/practice/complete-session`;
          
          const fallbackResponse = await fetch(fallbackEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(resultData)
          });
          
          if (fallbackResponse.ok) {
            const fallbackResult = await fallbackResponse.json();
            console.log('✅ Practice session saved via fallback endpoint:', fallbackResult);
            
            // Mark this as a learning path session completion for progress tracking
            fallbackResult.learning_path_session = true;
            fallbackResult.subtopic_id = resultData.subtopic_id;
            fallbackResult.difficulty_level = resultData.difficulty_level;
            fallbackResult.learning_path_id = resultData.learning_path_id;
            
            return fallbackResult;
          }
        }
        
        throw new Error(`Failed to save session: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Practice session saved successfully:', result);

    } catch (err) {
      console.error('Error saving practice session:', err);
      // Continue to show results even if save fails
    }

    setShowResults(true);
  };

  const pauseSession = async () => {
    if (window.confirm('Are you sure you want to pause this session? You can resume it later.')) {
      setIsTimerActive(false);
      setIsPaused(true);
      
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const currentTimeSpent = totalTimeSpent + (totalDuration - timeRemaining);
        
        // Check if this is a Learning Path practice session by looking at session data
        const isLearningPathSession = sessionInfo.subtopic_id || sessionInfo.learning_path_id;
        const pauseEndpoint = isLearningPathSession 
          ? `${backendUrl}/api/learning-path/practice/pause-session`
          : `${backendUrl}/api/practice/pause-session`;
          
        console.log('🔍 Using pause endpoint:', pauseEndpoint, 'for session:', sessionInfo);
        
        await fetch(pauseEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            session_id: sessionInfo.id,
            user_id: user?.id,
            current_question_index: currentQuestionIndex,
            answers_so_far: answers,
            time_spent_seconds: currentTimeSpent
          })
        });
        
        // Navigate back to practice launcher
        onBack();
      } catch (err) {
        console.error('Error pausing session:', err);
        // Continue with local pause even if API fails
        onBack();
      }
    }
  };

  const skipToEnd = () => {
    if (window.confirm('Are you sure you want to finish this practice session early?')) {
      finishSession();
    }
  };

  if (questions.length === 0) {
    return (
      <div className="font-opensans text-text-dark bg-bg-cream min-h-screen flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-md">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-poppins font-bold text-primary mb-2">Session Error</h2>
          <p className="text-gray-600 mb-6">
            No questions were generated for this practice session. Please try again.
          </p>
          <button
            onClick={onBack}
            className="bg-primary hover:bg-blue-800 text-white px-6 py-3 rounded-lg transition-all transform hover:scale-105 font-medium"
          >
            Back to Practice Setup
          </button>
        </div>
      </div>
    );
  }

  if (showResults) {
    return (
      <div className="font-opensans text-text-dark bg-bg-cream min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg w-full text-center">
          <div className="text-6xl mb-4">
            💪
          </div>

          <h2 className="text-2xl font-poppins font-bold mb-2" style={{ color: 'var(--mc-primary)' }}>
            Practice Complete!
          </h2>

          <div className="text-4xl font-bold mb-2" style={{ color: 'var(--mc-error, #EF4444)' }}>
            {sessionScore}%
          </div>

          <div className="grid grid-cols-2 gap-4 my-6 text-sm">
            <div className="rounded-lg p-3" style={{ background: 'var(--mc-primary-lighter)' }}>
              <div className="font-medium" style={{ color: 'var(--mc-gray-600)' }}>Questions Answered</div>
              <div className="text-xl font-bold" style={{ color: 'var(--mc-charcoal)' }}>
                {Object.keys(answers).length}/{questions.length}
              </div>
            </div>

            <div className="rounded-lg p-3" style={{ background: 'var(--mc-primary-lighter)' }}>
              <div className="font-medium" style={{ color: 'var(--mc-gray-600)' }}>Correct Answers</div>
              <div className="text-xl font-bold" style={{ color: 'var(--mc-primary)' }}>
                {questions.filter((question, index) => {
                  const userAnswer = answers[index];
                  let correctAnswerIndex;
                  if (question.correct_index !== undefined) {
                    correctAnswerIndex = question.correct_index;
                  } else if (typeof question.correct_answer === 'string' && question.correct_answer.match(/^[A-D]$/)) {
                    // Convert letter-based answers (A, B, C, D) to indices (0, 1, 2, 3)
                    correctAnswerIndex = question.correct_answer.charCodeAt(0) - 'A'.charCodeAt(0);
                  } else {
                    // Try to find exact text match in options
                    correctAnswerIndex = question.options.indexOf(question.correct_answer);
                  }
                  return userAnswer === correctAnswerIndex;
                }).length}
              </div>
            </div>

            <div className="rounded-lg p-3" style={{ background: 'var(--mc-primary-lighter)' }}>
              <div className="font-medium" style={{ color: 'var(--mc-gray-600)' }}>Subtopic</div>
              <div className="text-sm font-bold" style={{ color: 'var(--mc-charcoal)' }}>
                {sessionInfo.subtopic}
              </div>
            </div>

            <div className="rounded-lg p-3" style={{ background: 'var(--mc-primary-lighter)' }}>
              <div className="font-medium" style={{ color: 'var(--mc-gray-600)' }}>Time Used</div>
              <div className="text-sm font-bold" style={{ color: 'var(--mc-primary)' }}>
                {Math.round((totalDuration - timeRemaining) / 60)} min
              </div>
            </div>
          </div>

          <div className="rounded-lg p-4 mb-6" style={{
            background: 'var(--mc-primary-lighter)',
            border: '1px solid var(--mc-primary-light)'
          }}>
            <p className="text-sm" style={{ color: 'var(--mc-gray-600)' }}>
              <strong>Great practice session!</strong>
              {' '}Don't worry, practice makes perfect!
              Try focusing on areas that need improvement. 💪
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => onComplete(sessionScore)}
              className="w-full text-white font-semibold py-3 px-4 rounded-lg transition-all transform hover:scale-105"
              style={{ background: 'var(--mc-primary)' }}
              onMouseEnter={(e) => e.target.style.background = 'var(--mc-primary-dark)'}
              onMouseLeave={(e) => e.target.style.background = 'var(--mc-primary)'}
            >
              Continue Practice
            </button>

            <button
              onClick={onBack}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const hasAnswer = answers[currentQuestionIndex] !== undefined;
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const progressPercentage = ((currentQuestionIndex + 1) / questions.length) * 100;
  
  // Timer progress for the circular progress indicator
  const timerProgressPercentage = timeRemaining !== null && totalDuration > 0 
    ? Math.round((timeRemaining / totalDuration) * 100) 
    : 0;

  // Timer color based on remaining time
  const getTimerColor = () => {
    if (timeRemaining === null || totalDuration === 0) return 'text-gray-400';
    const percentageLeft = (timeRemaining / totalDuration) * 100;
    if (percentageLeft > 50) return 'text-green-600';
    if (percentageLeft > 20) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <div className="font-opensans text-text-dark bg-bg-cream min-h-screen">
      {/* Universal Header */}
      <UniversalHeader
        currentPage="practice"
        practiceSessionActive={true}
        selectedTopic={{ name: sessionInfo.subtopic }}
        breadcrumbs={[
          { label: 'Dashboard', action: null, current: false },
          { label: 'Learning Paths', action: null, current: false },
          { label: sessionInfo.subtopic || 'Practice', action: null, current: false },
          { label: 'Practice Session', action: null, current: true }
        ]}
      />
      
      {/* Practice Session Header with Timer and Progress */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={pauseSession}
                className="mr-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Pause session"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6" />
                </svg>
              </button>
              <button
                onClick={skipToEnd}
                className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Finish early"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-poppins font-bold text-primary">
                  {sessionInfo.subtopic} Practice
                </h1>
                <p className="text-gray-600">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-6">
              {/* Timer */}
              <div className="text-right">
                <div className={`text-2xl font-bold ${getTimerColor()}`}>
                  {formatTime(timeRemaining)}
                </div>
                <div className="text-xs text-gray-500">remaining</div>
              </div>

              {/* Progress Circle */}
              <div className="relative w-16 h-16">
                <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 32 32">
                  <circle
                    cx="16"
                    cy="16"
                    r="14"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    className="text-gray-200"
                  />
                  <circle
                    cx="16"
                    cy="16"
                    r="14"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeDasharray={`${timerProgressPercentage} ${100 - timerProgressPercentage}`}
                    className="text-primary transition-all duration-300"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-bold text-gray-700">
                    {Math.round(progressPercentage)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Question Display */}
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <EnhancedQuestionDisplay
            question_text={currentQuestion.question_text}
            options={currentQuestion.options}
            selectedAnswer={answers[currentQuestionIndex]}
            onAnswerSelect={(answerIndex) => handleAnswerSelect(currentQuestionIndex, answerIndex)}
            explanation={currentQuestion.explanation}
            hint={currentQuestion.hint}
            showExplanation={false}
            has_diagram={false}
            diagram_svg={null}
            isTestMode={true}
          />

          {/* Navigation */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <button
                onClick={previousQuestion}
                disabled={currentQuestionIndex === 0}
                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentQuestionIndex === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>

              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-600">
                  {hasAnswer ? '✅ Answered' : '⏳ Select an answer to continue'}
                </div>

                {/* Skip Question */}
                <button
                  onClick={nextQuestion}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Skip Question
                </button>
              </div>

              <button
                onClick={nextQuestion}
                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all transform hover:scale-105 ${
                  isLastQuestion
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-primary hover:bg-blue-800 text-white'
                }`}
              >
                {isLastQuestion ? 'Finish Practice' : 'Next'}
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticeSession;